import React from 'react';
import PersonalInfoBlock from '../../../components/FormComponents/PersonalInfoBlock';
import IdentificationNumberBlock from '../../../components/FormComponents/IdentificationNumberBlock';
import CountryInfoBlock from '../../../components/FormComponents/CountryInfoBlock';
import { AddRemoveBlock } from '~/components/AddRemoveBlock/AddRemoveBlock';
import { AddressInfo } from '../../../components/FormComponents/AddressInfo';
import { Grid, Typography } from '@mui/material';
import { FormInput } from '~/components/FormComponents/FormInput';
import { DocumentInfo } from '../../../components/FormComponents/DocumentInfo';
import FormContainer from '~/modules/identification/components/FormComponents/FormContainer';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { OwnerType, TOwner, TOwnerLink, TOwnershipStructure } from '../types';
import { FormYesNoQuestion } from '~/components/FormComponents/FormYesNoQuestion';
import { getEmptyLink, getEmptyOwner } from '../utils/utils.tsx';
import { ROOT_OWNER_NODE } from '../constants.ts';
import FormGridItem from '~/components/FormComponents/FormGridItem';

const OwnerPersonInfo = () => {
  const { control, setValue, getValues } = useFormContext<TOwnershipStructure>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'owners',
    keyName: 'fieldId',
  });

  const link = useWatch({
    control,
    name: 'link',
  });

  const lastIndex = fields.length - 1;

  const handleOwnershipPercentage = () => {
    const total =
      getValues('link')?.reduce((sum: number, link: TOwnerLink) => {
        const value = Number(link.ownershipPercentage || '0') || 0;
        return sum + value;
      }, 0) || 0;

    const roundedTotal = Number(total.toFixed(2));

    setValue('ownershipPercentageTotal', roundedTotal, { shouldValidate: true });
  };

  const handleRemove = (index: number) => {
    const owners = getValues('owners');
    const links = getValues('link');
    const ownerId = owners[index]?.id;
    const filteredLinks = links?.filter((item) => item.fromNodeId !== ownerId);
    remove(index);
    setValue('link', filteredLinks, { shouldDirty: true });
    handleOwnershipPercentage();
  };

  const handleAddOwner = () => {
    const newOwner = getEmptyOwner(OwnerType.INDIVIDUAL);
    const links = getValues('link');
    append(newOwner);
    setValue('link', [...links, getEmptyLink(newOwner.id, ROOT_OWNER_NODE)], { shouldDirty: true });
  };

  return (
    <>
      <Typography variant="h5" sx={{ textAlign: 'center' }}>
        Відомості про структуру власності
      </Typography>
      {fields?.map((owner: TOwner, index: number) => {
        const linkIndex = link.findIndex((item) => item.fromNodeId === owner.id);
        return (
          <FormContainer title={`Засновник / учасник ${index + 1}`} hasBookmark key={owner.id}>
            <PersonalInfoBlock section={`owners.${index}`} />
            <IdentificationNumberBlock section={`owners.${index}`} />
            <CountryInfoBlock section={`owners.${index}`} />
            <DocumentInfo
              citizenshipCountryPath={`owners.${index}.citizenshipCountry`}
              residenceCountryPath={`owners.${index}.residenceCountry`}
              section={`owners.${index}`}
            />
            <AddressInfo section={`owners.${index}`} />
            <FormGridItem sx={{ alignContent: 'flex-end' }}>
              <FormInput
                name={`link.${linkIndex}.ownershipPercentage`}
                placeholder="Введіть частку в статутному капіталі"
                label="Частка в статутному капіталі (%)"
                fullWidth
                onChange={handleOwnershipPercentage}
              />
            </FormGridItem>
            <FormGridItem sx={{ alignContent: 'flex-end' }}>
              <FormYesNoQuestion
                name={`owners.${index}.isBeneficiary`}
                question="Чи є особа кінцевим бенефіціарним власником?"
              />
            </FormGridItem>
            <Grid size={12}>
              <AddRemoveBlock
                add={handleAddOwner}
                remove={() => handleRemove(index)}
                isPlusShown={lastIndex !== 29}
                isMinusShown={lastIndex !== 0}
                plusText={'Натисніть на "+" для додавання відомості про структуру власності'}
                minusText={'Натисніть на "-" для видалення відомості про структуру власності'}
              />
            </Grid>
          </FormContainer>
        );
      })}
    </>
  );
};
export default React.memo(OwnerPersonInfo);
